import { list as listIntegrationProperties } from '@/services/api/resources/integrationProperty/actions';
import emitter from '@/services/emitter';
import { integrationsService } from '@/services/integrations/integrationsService';
import PrismaticClient from '@/services/integrations/prismaticClient';
import { Integration, IntegrationID } from '@/services/integrations/types';
import { taskToPromise } from '@/ui/helpers/fpTsHelper';
import _ from 'lodash';
import { defineStore } from 'pinia';
import { IntegrationProperty } from 'src/entities/integrationProperty';

interface IntegrationsHomeState {
  integrations: Integration[];
  activeCategory: string | null;
  showAll: boolean;
  searchIndex: string[];
  search: string;
  loading: boolean;
}

export const useIntegrationsHomeStore = defineStore('integrationsHome', {

  state: (): IntegrationsHomeState => {
    return ({
      integrations: [],
      activeCategory: null,
      showAll: true,
      searchIndex: [],
      search: '',
      loading: false,
    });
  },

  getters: {
    categories: state => _(state.integrations).map('category').uniq().sort().value(),

    selectedIntegrations(state): Integration[] {
      const categoryFilter = (integration: Integration) => {
        if (state.activeCategory === null) {
          return true;
        }

        return integration.category === state.activeCategory;
      };
      const activatedFilter = (integration: Integration) => {
        if (state.showAll) {
          return true;
        }

        return integrationsService.isActivated(integration);
      };
      const searchFilter = (integration: Integration, i: number) => {
        if (state.search === '') {
          return true;
        }

        return state.searchIndex[i].includes(state.search);
      };

      return state.integrations.filter((integration, i) => {
        return categoryFilter(integration)
          && activatedFilter(integration)
          && searchFilter(integration, i);
      });
    },

    isActiveCategory: state => (category: string|null) => {
      return state.activeCategory === category;
    },

    shouldShowUpgradeAlert: state => {
      return state.integrations.some(i => !i.enabled);
    },
  },

  actions: {
    async loadIntegrations() {
      if (this.integrations.length) {
        return;
      }

      this.loading = true;
      this.integrations = await this.fillUpProperties(await PrismaticClient.getIntegrations());
      this.loading = false;
      this.buildSearchIndex();

      emitter.on('integrationUpdated', this.updateIntegration);
    },

    async fillUpProperties(integrations: Integration[]) {
      const properties = await taskToPromise(listIntegrationProperties);
      const id2properties: Record<IntegrationID, IntegrationProperty> = _.keyBy(properties, 'prismatic_id');

      integrations.forEach(i => {
        const integrationId = i.firstVersion.id;

        i.description = id2properties[integrationId]?.overview || i.description;
        i.name = id2properties[integrationId]?.name || i.name;
        i.category = id2properties[integrationId]?.category || i.category;
        i.enabled = id2properties[integrationId]?.enabled;
      });

      return integrations;
    },

    updateIntegration(integration: Integration) {
      const i = this.integrations.findIndex(i => i.id === integration.id);
      if (i !== -1) {
        this.integrations = [...this.integrations.slice(0, i), integration, ...this.integrations.slice(i + 1)];
      }
    },

    selectCategory(category: string|null) {
      this.activeCategory = category;
    },

    setSearch(search: string) {
      this.search = search.toLowerCase();
    },

    buildSearchIndex() {
      this.searchIndex = this.integrations.map(integration => {
        return `${integration.name} ${integration.description} ${integration.category} ${integration.overview}`.toLowerCase();
      });
    },

  },
});
